import React, { useState, useEffect, useRef } from "react";
import ExpoTotemCell from "./ExpoTotemCell";
import EInkSmartLabel from "./EInkSmartLabel";

// Durate delle due meta' del cambio carta: la vecchia che se ne va e la nuova che arriva. Devono
// coprire le animazioni in expo_totems.scss, dove l'etichetta va oltre la carta: in uscita
// l'inchiostro si scarica a scatti, in entrata le righe si riscrivono una dopo l'altra
// Misura con cui chiedere l'immagine della carta. Senza immagine grande si ripiega su quella piccola,
// ma se non ce n'e' nessuna il segnaposto va chiesto grande lo stesso: a 108x150 resterebbe un
// francobollo dentro uno slot alto 350
const imageSizeFor = (cell) => {
  if (cell?.blueprint_image?.large_image_url) return 'large';
  if (cell?.blueprint_image?.shrinked_medium_image_url) return 'shrinked_medium';

  return 'large';
};

const EXIT_DURATION = 780;
const ENTER_DURATION = 1450;

const ExpoTotemCellContainer = ({ isDimmed, cell, onLabelClick, cellSize = 'large', eInkSmartLabelOnHoverMouceIcon }) => {
  const isSmall = cellSize === 'small';

  // La carta che se ne va deve restare a schermo per il tempo di uscire, quindi si mostra il
  // contenuto precedente finche' l'animazione non e' finita, e solo dopo si passa a quello nuovo
  const [displayedCell, setDisplayedCell] = useState(cell);
  const [phase, setPhase] = useState('idle');
  const [ghostRole, setGhostRole] = useState(null);
  const displayedProductIdRef = useRef(cell?.product_id);

  useEffect(() => {
    const previousProductId = displayedProductIdRef.current;

    // Stessa carta: prezzo ed etichetta si aggiornano subito, senza animazione
    if (cell?.product_id === previousProductId) {
      setDisplayedCell(cell);
      return;
    }

    // Carta che arriva: il posto vuoto non se ne va, viene coperto. Resta fermo e si dissolve mentre
    // la carta entra, quindi la carta entra subito e non c'e' nessuna uscita da aspettare
    if (cell?.product_id && !previousProductId) {
      displayedProductIdRef.current = cell?.product_id;
      setDisplayedCell(cell);
      setGhostRole('disappearing');
      setPhase('entering');
      return;
    }

    // Carta che se ne va: il posto vuoto e' lo spazio sotto la carta, non un elemento che arriva.
    // Affiora mentre la carta si rimpicciolisce, e alla fine resta li' senza nessuna entrata in scena
    const isEmptying = !cell?.product_id;
    setGhostRole(isEmptying ? 'appearing' : null);
    setPhase('exiting');

    const timer = setTimeout(() => {
      displayedProductIdRef.current = cell?.product_id;
      setDisplayedCell(cell);
      setGhostRole(null);
      setPhase(isEmptying ? 'idle' : 'entering');
    }, EXIT_DURATION);

    return () => clearTimeout(timer);
  }, [cell]);

  useEffect(() => {
    if (phase !== 'entering') return;

    const timer = setTimeout(() => {
      setPhase('idle');
      setGhostRole(null);
    }, ENTER_DURATION);
    return () => clearTimeout(timer);
  }, [phase]);

  const hasProduct = displayedCell && displayedCell.product_id;

  return <div
    className={
      `loop-cell-wrapper${isDimmed ? ' loop-cell-dimmed' : ''}` +
      `${isSmall ? ' loop-cell-wrapper--small' : ''}` +
      `${phase !== 'idle' ? ` loop-cell-wrapper--${phase}` : ''}`
    }
  >
    <div className="loop-cell-card">
      {/* Il segnaposto che svanisce lo disegna lo stesso componente della cella vuota: ridisegnarlo a
          mano significherebbe rincorrerne le misure, e basta uno scarto per vederlo deformato */}
      {ghostRole && (
        <div className={`loop-cell-ghost loop-cell-ghost--${ghostRole}`}>
          <ExpoTotemCell cellSize={cellSize} />
        </div>
      )}
      <div className="loop-cell-content">
        <ExpoTotemCell
          grading={displayedCell && displayedCell.grading}
          blueprintImage={displayedCell && displayedCell.blueprint_image}
          cellState={displayedCell && displayedCell.state}
          label={displayedCell?.label}
          serialNumber={displayedCell?.serial_number}
          imageSize={imageSizeFor(displayedCell)}
          cellSize={cellSize}
        />
      </div>
    </div>
    <EInkSmartLabel
      cell={displayedCell}
      onLabelClick={onLabelClick && hasProduct ? () => onLabelClick(displayedCell) : undefined}
      onHoverMouceIcon={hasProduct ? eInkSmartLabelOnHoverMouceIcon : undefined}
    />
  </div>
};

export default ExpoTotemCellContainer;
